import {
  Directive,
  ElementRef,
  PLATFORM_ID,
  DestroyRef,
  afterNextRender,
  inject,
  input,
  output,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

@Directive({
  selector: '[appInView]',
  standalone: true,
})
export class InViewDirective {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * ScrollTrigger start position (default: 'top 85%')
   */
  readonly start = input<string>('top 85%');

  /**
   * Whether to trigger only once (default: true)
   */
  readonly once = input<boolean>(true);

  /**
   * Fallback threshold for IntersectionObserver
   */
  readonly threshold = input<number>(0.15);

  /**
   * Emitted when element enters viewport
   */
  readonly inView = output<void>();

  private scrollTrigger: ScrollTrigger | null = null;
  private observer: IntersectionObserver | null = null;

  constructor() {
    afterNextRender(() => {
      if (!this.isBrowser) return;

      const element = this.elementRef.nativeElement;
      if (!element) return;

      if (
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function'
      ) {
        gsap.registerPlugin(ScrollTrigger);

        this.scrollTrigger = ScrollTrigger.create({
          trigger: element,
          start: this.start(),
          once: this.once(),
          onEnter: () => {
            this.emitInView();
          },
        });
      } else if (typeof IntersectionObserver !== 'undefined') {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.emitInView();
                if (this.once()) {
                  this.observer?.disconnect();
                }
              }
            });
          },
          { threshold: this.threshold() },
        );
        this.observer.observe(element);
      } else {
        this.emitInView();
      }
    });

    this.destroyRef.onDestroy(() => {
      if (this.isBrowser) {
        if (this.scrollTrigger) {
          this.scrollTrigger.kill();
        }
        if (this.observer) {
          this.observer.disconnect();
        }
      }
    });
  }

  private emitInView(): void {
    this.inView.emit();
  }
}
